<template>
  <BaseScreen v-bind="screen">
    <template #actions>
      <BaseButton
        v-for="(item, i) in actions"
        :key="`action_${i}`"
        v-bind="item"
        @click="item.action"
      />
    </template>
    <BaseTimer
      ref="timer"
      slot="title"
      :style="{ visibility: isTimerStarted ? 'visible' : 'hidden' }"
      @start="onTimerStart"
      @stop="onTimerStop"
      @finish="onTimerFinish"
    />
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Camera>
      <PhotoFrames
        v-if="isPhotoFramesVisible"
        v-bind="photoframes"
        slot="overlay"
        @prev="prevFrame"
        @next="nextFrame"
      />
      <div
        v-else-if="isPhotoReady"
        class="camera__image"
        slot="overlay"
      >
        <img :src="photo.base64" alt="" />
      </div>
    </Camera>
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import cameraMixin from '@/mixins/camera';
import photoPrintMixin from '@/mixins/photoPrint';
import photoFrameMixin, { FRAME_POSITION, CORNER_MARGIN } from '@/mixins/photoFrame';

import BaseButton from '@/components/BaseButton.vue';
import BaseTimer from '@/components/BaseTimer.vue';
import PhotoFrames from '@/components/PhotoFrames.vue';
import Camera from '@/components/Camera.vue';

import frame from '@/assets/images/frame.png';

export default {
  name: 'PhotoTakeScreen',
  mixins: [screenMixin, cameraMixin, photoPrintMixin, photoFrameMixin],
  components: {
    BaseButton,
    BaseTimer,
    Camera,
    PhotoFrames,
  },
  data() {
    return {
      isTimerStarted: false,
      isPrintStarted: false,
      isCameraStarted: false,
      isPhotoFramesVisible: false,
      isPhotoFrameSelected: false,
      isPrintError: false,
      hasPrinter: true,
      frameIndex: 0,
    };
  },
  staticData: () => ({
    frames: [
      {
        src: frame,
        position: FRAME_POSITION.BOTTOM,
      },
      {
        src: frame,
        position: FRAME_POSITION.TOP,
      },
    ],
  }),
  computed: {
    ...mapState('photo', [
      'photo',
    ]),
    pendingText() {
      if (this.isPrintStarted) {
        return this.$t('photo.printPending');
      }
      if (!this.isCameraStarted) {
        return this.$t('photo.cameraPending');
      }
      return this.$t('photo.processing');
    },
    screenCase() {
      if (this.isThanksVisible) {
        return 'STEP_PHOTO_PRINTED';
      }
      if (this.isErrorVisible) {
        return 'STEP_HARDWARE_ERROR';
      }
      if (!this.isCameraStarted || this.hasActiveOperation) {
        return null;
      }
      if (this.isPhotoReady) {
        return 'STEP_PHOTO_READY';
      }
      return 'STEP_TAKE_PHOTO';
    },
    actions() {
      if (this.isTimerStarted) {
        return [
          {
            text: this.$t('photo.cancel'),
            action: this.stopTimer,
            theme: 'secondary',
          },
        ];
      }
      if (this.isPhotoFramesVisible) {
        return [
          this.isPhotoFrameSelected ? {
            text: this.$t('photo.removeFrame'),
            action: this.removePhotoFrame,
            theme: 'secondary',
          } : {
            text: this.$t('photo.cancel'),
            action: this.hidePhotoFrames,
            theme: 'secondary',
          },
          {
            text: this.$t('photo.pick'),
            action: this.selectPhotoFrame,
          },
        ];
      }
      if (this.isPhotoReady) {
        return [
          {
            text: this.$t('photo.reshoot'),
            action: this.reshoot,
            theme: 'secondary',
          },
          {
            text: this.$t('photo.send'),
            action: this.goToPhotoSendScreen,
          },
          this.hasPrinter ? {
            text: this.$t('photo.print'),
            action: this.printPhoto,
          } : null,
          {
            text: this.$t('photo.selectFrame'),
            action: this.showPhotoFrames,
          },
        ].filter(Boolean);
      }
      return [
        {
          text: this.$t('photo.take'),
          action: this.startTimer,
        },
      ];
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          view: 'circle',
          size: 'l',
        },
        title: {
          text: this.$t('photo.pickUp'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('photo.goHome'),
            action: this.goToHomeScreen,
          },
          {
            text: this.$t('photo.goBack'),
            action: this.backToPhoto,
          },
        ],
      };
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'error',
          view: 'circle',
          size: 'l',
        },
        title: {
          text: this.$t(this.isPrintError ? 'photo.printError' : 'photo.badHardware'),
          size: 'l',
        },
        description: {
          text: this.$t(this.isPrintError ? 'photo.printNotPossible' : 'photo.notPossible'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('photo.goHome'),
            action: this.goToHomeScreen,
          },
          this.isPhotoReady ? {
            text: this.$t('photo.send'),
            action: this.goToPhotoSendScreen,
          } : null,
        ].filter(Boolean),
      };
    },
    photoframes() {
      return {
        source: this.photo.original,
        frames: this.frames,
        frameIndex: this.frameIndex,
        cornerMargin: CORNER_MARGIN,
      };
    },
    hasActiveOperation() {
      return this.isPending || this.isPrintStarted || this.isTimerStarted || this.isPhotoFramesVisible;
    },
    isPhotoReady() {
      return Boolean(this.photo);
    },
  },
  created() {
    this.clearPhoto();
    this.startCamera();
    this.showPending(0);
    this.initActions([
      ['ACTION_TAKE_PHOTO', this.startTimer],
      ['ACTION_RESHOOT', this.reshoot],
      ['ACTION_PRINT_PHOTO', this.printPhoto],
      ['ACTION_CANCEL_PHOTO', this.cancelOperation],
      ['ACTION_SEND_PHOTO', this.goToPhotoSendScreen],
      ['ACTION_PICK_FRAME', this.selectPhotoFrame],
      ['ACTION_SHOW_PHOTO_FRAMES', this.showPhotoFrames],
      ['ACTION_BACK_TO_PHOTO', this.backToPhoto],
      ['ACTION_PREV_FRAME', this.prevFrame],
      ['ACTION_NEXT_FRAME', this.nextFrame],
    ]);
  },
  methods: {
    ...mapMutations('photo', [
      'setPhoto',
      'clearPhoto',
    ]),
    printPhoto() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.startPhotoPrint(this.photo.fullpath); // функция из миксина
    },
    showPhotoFrames() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.isPhotoFramesVisible = true;
    },
    hidePhotoFrames() {
      this.isPhotoFramesVisible = false;
    },
    async selectPhotoFrame() {
      if (!this.isPhotoFramesVisible) return;
      const selectedFrame = this.frames[this.frameIndex];
      const photoWithFrame = await this.drawFrame(this.photo.original, selectedFrame);
      this.photo.base64 = photoWithFrame;
      this.savePhoto(this.photo);
      this.isPhotoFrameSelected = true;
      this.hidePhotoFrames();
    },
    removePhotoFrame() {
      if (!this.isPhotoFramesVisible) return;
      this.photo.base64 = this.photo.original;
      this.savePhoto(this.photo);
      this.isPhotoFrameSelected = false;
      this.hidePhotoFrames();
    },
    prevFrame() {
      const lastIndex = this.frames.length - 1;
      const prevIndex = this.frameIndex - 1;
      this.frameIndex = (prevIndex < 0) ? lastIndex : prevIndex;
    },
    nextFrame() {
      const lastIndex = this.frames.length - 1;
      const nextIndex = this.frameIndex + 1;
      this.frameIndex = (nextIndex > lastIndex) ? 0 : nextIndex;
    },
    reshoot() {
      if (this.hasActiveOperation) return;
      this.clearPhoto();
      this.startTimer();
    },
    backToPhoto() {
      if (!this.isThanksVisible) return;
      this.hideThanks();
    },
    cancelOperation() {
      if (this.isTimerStarted) {
        this.stopTimer();
        return;
      }
      if (this.isPhotoFramesVisible) {
        const action = this.isPhotoFrameSelected
          ? this.removePhotoFrame
          : this.hidePhotoFrames;
        action();
      }
    },
    onNoPrinter() {
      // Этот колбек будет вызван из миксина, если в роботе нет принтера
      this.hasPrinter = false;
    },
    onPhotoPrintStarted() {
      // Этот колбек будет вызван из миксина после начала печати
      this.isPrintStarted = true;
      this.showPending();
    },
    onPhotoPrintFinished() {
      // Этот колбек будет вызван из миксина после окончания печати
      this.isPrintStarted = false;
      this.isCameraStarted = false;
      this.showThanks();
    },
    onPhotoPrintError() {
      // Этот колбек будет вызван из миксина при ошибке печати
      this.isPrintError = true;
      this.isPrintStarted = false;
      this.showError();
    },
    onCameraStart() {
      this.isCameraStarted = true;
      this.hidePending();
    },
    onPhotoReady(photo) {
      this.setPhoto(photo);
      this.hidePending();
    },
    onTimerStart() {
      this.isTimerStarted = true;
    },
    onTimerStop() {
      this.isTimerStarted = false;
    },
    onTimerFinish() {
      this.takePhoto(); // функция из миксина
      this.showPending();
    },
    onCameraError() {
      // Этот колбек будет вызван из миксина, если прилетит ошибка камеры
      this.showError();
    },
    startTimer() {
      return this.$refs.timer?.start();
    },
    stopTimer() {
      return this.$refs.timer?.stop();
    },
    goToPhotoSendScreen() {
      if (!this.isPhotoReady || this.hasActiveOperation) return;
      this.$router.push(this.$router.options.paths.photoSend);
    },
  },
};
</script>
